import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

// ── Reusable QR Scanner overlay ──────────────────────────────
function QRScanner({ onScan, onClose }) {
  const [error, setError]       = useState('');
  const [torchOn, setTorchOn]   = useState(false);
  const [cameras, setCameras]   = useState([]);
  const [camIdx, setCamIdx]     = useState(0);
  const scannerRef              = useRef(null);
  const containerRef            = useRef(null);
  const scannedRef              = useRef(false);

  // Create a short beep using Web Audio API
  const playBeep = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 1200;
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + 0.12);
    } catch {}
  }, []);

  const stopScanner = useCallback(async () => {
    try {
      if (scannerRef.current) {
        const state = scannerRef.current.getState();
        if (state === 2) await scannerRef.current.stop(); // SCANNING
        scannerRef.current.clear();
      }
    } catch {}
    scannerRef.current = null;
  }, []);

  const startScanner = useCallback(async (cameraId) => {
    await stopScanner();
    if (!containerRef.current) return;

    try {
      const scanner = new Html5Qrcode('qr-reader-region');
      scannerRef.current = scanner;

      await scanner.start(
        cameraId || { facingMode: 'environment' },
        { fps: 15, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
        (decodedText) => {
          if (scannedRef.current) return;
          scannedRef.current = true;
          // Vibrate + beep
          if (navigator.vibrate) navigator.vibrate(200);
          playBeep();
          // Small delay for visual feedback
          setTimeout(() => {
            stopScanner();
            onScan(decodedText);
          }, 300);
        },
        () => {} // ignore scan failures
      );

      // Try to enable torch
      if (torchOn) {
        try {
          const track = scanner.getRunningTrackCameraCapabilities?.();
          if (track?.torchFeature?.().isSupported?.()) {
            track.torchFeature().apply(true);
          }
        } catch {}
      }
    } catch (err) {
      const msg = String(err?.message || err || '');
      if (msg.includes('NotAllowed') || msg.includes('Permission')) {
        setError('permission');
      } else {
        setError(msg);
      }
    }
  }, [stopScanner, playBeep, onScan, torchOn]);

  // Enumerate cameras on mount
  useEffect(() => {
    Html5Qrcode.getCameras().then(devices => {
      setCameras(devices || []);
      if (devices.length > 0) {
        // Prefer back camera
        const backIdx = devices.findIndex(d => /back|rear|environment/i.test(d.label));
        const idx = backIdx >= 0 ? backIdx : 0;
        setCamIdx(idx);
        startScanner(devices[idx].id);
      } else {
        startScanner(null);
      }
    }).catch(() => {
      startScanner(null);
    });

    return () => { stopScanner(); };
    // eslint-disable-next-line
  }, []);

  const flipCamera = async () => {
    if (cameras.length < 2) return;
    const next = (camIdx + 1) % cameras.length;
    setCamIdx(next);
    await startScanner(cameras[next].id);
  };

  const toggleTorch = async () => {
    const next = !torchOn;
    setTorchOn(next);
    try {
      const track = scannerRef.current?.getRunningTrackCameraCapabilities?.();
      if (track?.torchFeature?.().isSupported?.()) {
        track.torchFeature().apply(next);
      }
    } catch {}
  };

  const retryPermission = () => {
    setError('');
    scannedRef.current = false;
    startScanner(cameras[camIdx]?.id || null);
  };

  // Permission denied UI
  if (error === 'permission') {
    return (
      <div className="qr-overlay">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📷</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Camera Access Required</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 24, maxWidth: 320, lineHeight: 1.5 }}>
            Camera access is required to scan QR codes. Please allow camera access in your browser settings.
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn btn-primary" onClick={retryPermission}>Try Again</button>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="qr-overlay">
      {/* Top bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, display: 'flex', justifyContent: 'space-between', padding: '16px 20px' }}>
        <button onClick={onClose} className="qr-ctrl-btn" aria-label="Close">✕</button>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={toggleTorch} className={`qr-ctrl-btn${torchOn ? ' active' : ''}`} aria-label="Toggle torch">
            {torchOn ? '🔦' : '💡'}
          </button>
          {cameras.length > 1 && (
            <button onClick={flipCamera} className="qr-ctrl-btn" aria-label="Flip camera">🔄</button>
          )}
        </div>
      </div>

      {/* Camera feed */}
      <div ref={containerRef} id="qr-reader-region" className="qr-camera-feed" />

      {/* Scanning frame overlay */}
      <div className="qr-frame-container">
        <div className="qr-frame">
          <div className="qr-corner qr-corner-tl" />
          <div className="qr-corner qr-corner-tr" />
          <div className="qr-corner qr-corner-bl" />
          <div className="qr-corner qr-corner-br" />
          <div className="qr-scan-line" />
        </div>
      </div>

      {/* Bottom text */}
      <div style={{ position: 'absolute', bottom: 60, left: 0, right: 0, textAlign: 'center', zIndex: 10 }}>
        <div style={{ fontSize: 14, color: '#fff', fontWeight: 600, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
          Point camera at QR code on the unit
        </div>
        {error && error !== 'permission' && (
          <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 8 }}>{error}</div>
        )}
      </div>
    </div>
  );
}

export default QRScanner;
